import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { createCheckoutSession } from '@/lib/stripe';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles, Zap } from 'lucide-react';
import { useAuth } from '@/router';
import { useNavigate } from 'react-router-dom';

interface SubscriptionButtonProps {
  priceId: string;
  children: React.ReactNode;
  mode?: 'payment' | 'subscription';
  className?: string;
}

export function SubscriptionButton({ 
  priceId, 
  children, 
  mode = 'payment', 
  className 
}: SubscriptionButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { session } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async () => {
    // Check if user is logged in
    if (!session) {
      toast({
        title: "로그인 필요",
        description: "결제를 진행하려면 로그인이 필요합니다.",
      });
      navigate('/auth');
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('Starting checkout process for priceId:', priceId, 'mode:', mode);
      
      // Pass the mode to createCheckoutSession
      await createCheckoutSession(priceId, mode);
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "오류",
        description: '결제 과정을 시작하는데 실패했습니다. 다시 시도해주세요.',
        variant: 'destructive',
      });
      
      // Reset loading state
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleSubscribe}
      disabled={isLoading}
      className={cn(
        "w-full font-medium shadow transition-all duration-200 hover:shadow-lg", 
        className
      )}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          처리 중...
        </>
      ) : (
        <>
          {mode === 'subscription' && <Sparkles className="mr-2 h-4 w-4" />}
          {mode === 'payment' && <Zap className="mr-2 h-4 w-4" />}
          {children}
        </>
      )}
    </Button>
  );
}